import { useEffect, useState, useTransition } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { callApi, ShopifyAppApiError } from '@/lib/api';
import { BrandCard } from './buds-header';
import { establishesInitialSync } from './sync-ui-state';

type ShopifySyncMode = 'FULL' | 'MERGE';
type ShopifySyncRunStatus = 'RUNNING' | 'SUCCESS' | 'PARTIAL' | 'FAILED';

interface ShopifySyncError {
  externalId: string;
  message: string;
}

interface ShopifySyncSummary {
  runId: string;
  created: number;
  updated: number;
  unassigned: number;
  errors: ShopifySyncError[];
  durationMs: number;
}

interface ShopifySyncRunCounts {
  created: number;
  updated: number;
  unassigned: number;
  errors: number;
}

interface ShopifySyncRunSummary {
  id: string;
  mode: ShopifySyncMode;
  status: ShopifySyncRunStatus;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  counts: ShopifySyncRunCounts;
  errors: ShopifySyncError[];
  totalErrorCount: number;
  failureReason: string | null;
}

interface Props {
  initialSyncCompleted: boolean;
  onRunFinished: (establishedInitialSync: boolean) => void;
  onReconnectRequired: (message?: string) => void;
}

function formatErrorMessage(code: string): string {
  switch (code) {
    case 'NOT_CONNECTED':
      return 'No store is connected. Connect a Buds API key first.';
    case 'NOT_FOUND':
      return 'No Shopify connection on file. Try reconnecting the app.';
    case 'CONFLICT':
      return "Sync can't run right now. Make sure the app is still connected to Shopify; reconnect if you recently revoked the token.";
    case 'INVALID_KEY':
      return 'Buds rejected the saved API key. Reconnect with a fresh key.';
    case 'BUDS_UNREACHABLE':
      return 'Could not reach Buds. Try again in a moment.';
    default:
      return `Sync failed (${code}).`;
  }
}

function statusBadge(status: ShopifySyncRunStatus): {
  label: string;
  cls: string;
} {
  switch (status) {
    case 'SUCCESS':
      return { label: 'Success', cls: 'bg-emerald-100 text-emerald-800' };
    case 'PARTIAL':
      return { label: 'Partial', cls: 'bg-amber-100 text-amber-900' };
    case 'FAILED':
      return { label: 'Failed', cls: 'bg-red-100 text-red-900' };
    case 'RUNNING':
      return { label: 'Running…', cls: 'bg-blue-100 text-blue-900' };
  }
}

interface DisplaySummary {
  runId?: string;
  status: ShopifySyncRunStatus | null;
  finishedAt: string | null;
  counts: { created: number; updated: number; unassigned: number };
  durationMs: number | null;
  errors: ShopifySyncError[];
  totalErrorCount: number;
  failureReason: string | null;
}

function summaryFromTrigger(s: ShopifySyncSummary): DisplaySummary {
  return {
    runId: s.runId,
    status: s.errors.length === 0 ? 'SUCCESS' : 'PARTIAL',
    finishedAt: new Date().toISOString(),
    counts: {
      created: s.created,
      updated: s.updated,
      unassigned: s.unassigned,
    },
    durationMs: s.durationMs,
    errors: s.errors,
    totalErrorCount: s.errors.length,
    failureReason: null,
  };
}

function summaryFromRun(r: ShopifySyncRunSummary): DisplaySummary {
  return {
    runId: r.id,
    status: r.status,
    finishedAt: r.finishedAt,
    counts: {
      created: r.counts.created,
      updated: r.counts.updated,
      unassigned: r.counts.unassigned,
    },
    durationMs: r.durationMs,
    errors: r.errors,
    totalErrorCount: r.totalErrorCount,
    failureReason: r.failureReason,
  };
}

export function SyncProductsCard({
  initialSyncCompleted,
  onRunFinished,
  onReconnectRequired,
}: Props) {
  const [pending, startTransition] = useTransition();
  const [pendingMode, setPendingMode] = useState<ShopifySyncMode | null>(null);
  const [summary, setSummary] = useState<DisplaySummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingFull, setConfirmingFull] = useState(false);
  const [showAllErrors, setShowAllErrors] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const body = await callApi<{ run: ShopifySyncRunSummary | null }>(
          '/v1/shopify-app/sync/status',
          { method: 'GET' },
        );
        if (cancelled) return;
        if (body.run) {
          setSummary(summaryFromRun(body.run));
        }
      } catch {
        // ignore — the card just renders without prior state
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function run(mode: ShopifySyncMode) {
    setError(null);
    setSummary(null);
    setPendingMode(mode);
    try {
      const body = await callApi<ShopifySyncSummary>('/v1/shopify-app/sync', {
        method: 'POST',
        body: { mode },
      });
      setSummary(summaryFromTrigger(body));
      onRunFinished(establishesInitialSync(body));
    } catch (err) {
      const e = err as ShopifyAppApiError;
      if (e.code === 'NOT_CONNECTED') {
        onReconnectRequired(e.message);
        return;
      }
      setError(formatErrorMessage(e.code));
    } finally {
      setPendingMode(null);
    }
  }

  const disabled = pending;

  return (
    <BrandCard title="Sync products">
      <p className="text-sm text-buds-dark/70">
        Run a manual sync at any time to pull products in your sync scope from
        Shopify and turn them into Buds rewards.
      </p>

      {!initialSyncCompleted && (
        <div className="rounded-md border border-buds-sky/60 bg-buds-sky/10 px-3 py-2 text-xs text-buds-dark">
          Start here. Complete the first manual product sync before turning on
          automatic sync.
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={() => startTransition(() => run('MERGE'))}
          disabled={disabled}
          className="inline-flex flex-1 items-center justify-center rounded-full bg-buds-dark px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-buds-dark/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-buds-green focus-visible:ring-offset-2 focus-visible:ring-offset-buds-cream disabled:cursor-not-allowed disabled:opacity-60"
        >
          {pendingMode === 'MERGE' ? 'Syncing…' : 'Sync new & updated'}
        </button>
        <button
          type="button"
          onClick={() => setConfirmingFull(true)}
          disabled={disabled}
          className="inline-flex flex-1 items-center justify-center rounded-full border border-buds-dark/20 bg-white px-5 py-2.5 text-sm font-medium text-buds-dark transition hover:border-buds-dark/40 hover:bg-buds-dark/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-buds-dark/30 focus-visible:ring-offset-2 focus-visible:ring-offset-buds-cream disabled:cursor-not-allowed disabled:opacity-60"
        >
          {pendingMode === 'FULL' ? 'Replacing…' : 'Replace all'}
        </button>
      </div>

      {confirmingFull && (
        <div className="rounded-md border border-amber-300/60 bg-amber-50 px-3 py-3 text-xs text-amber-900">
          <p className="font-medium">Replace all synced rewards?</p>
          <p className="mt-1">
            Rewards previously synced from this store whose product is no longer
            in your sync scope will be unassigned from the store (and
            soft-deleted if this is the only store using them). Continue?
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => {
                setConfirmingFull(false);
                startTransition(() => run('FULL'));
              }}
              disabled={disabled}
              className="rounded-md bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800 disabled:opacity-60"
            >
              Yes, replace all
            </button>
            <button
              type="button"
              onClick={() => setConfirmingFull(false)}
              className="rounded-md border border-amber-700/40 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-400/60 bg-red-50 px-3 py-2 text-xs text-red-900">
          {error}
        </div>
      )}

      {summary && (
        <div className="space-y-2 rounded-md border border-buds-dark/10 bg-buds-surface/50 px-3 py-3 text-xs text-buds-dark">
          <div className="flex items-center justify-between">
            <span className="text-buds-dark/60">
              {summary.finishedAt
                ? `Last synced ${formatDistanceToNowStrict(new Date(summary.finishedAt), { addSuffix: true })}`
                : 'Just now'}
            </span>
            {summary.status && (
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                  statusBadge(summary.status).cls
                }`}
              >
                {statusBadge(summary.status).label}
              </span>
            )}
          </div>
          {summary.failureReason && (
            <div className="rounded-md border border-red-400/60 bg-red-50 px-2 py-1 text-red-900">
              {summary.failureReason}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Created" value={summary.counts.created} />
            <Stat label="Updated" value={summary.counts.updated} />
            <Stat label="Unassigned" value={summary.counts.unassigned} />
          </div>
          {summary.durationMs != null && (
            <p className="text-buds-dark/60">
              Completed in {summary.durationMs} ms.
            </p>
          )}
          {summary.errors.length > 0 && (
            <ErrorList
              errors={summary.errors}
              totalErrorCount={summary.totalErrorCount}
              expanded={showAllErrors}
              onToggle={() => setShowAllErrors((v) => !v)}
            />
          )}
        </div>
      )}
    </BrandCard>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-white px-3 py-2 shadow-sm">
      <div className="text-[10px] font-medium uppercase tracking-wide text-buds-dark/60">
        {label}
      </div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function ErrorList({
  errors,
  totalErrorCount,
  expanded,
  onToggle,
}: {
  errors: ShopifySyncError[];
  totalErrorCount: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const visible = expanded ? errors : errors.slice(0, 3);
  const truncated = totalErrorCount > errors.length;
  return (
    <div className="rounded-md border border-red-300/60 bg-red-50 px-3 py-2 text-red-900">
      <div className="flex items-center justify-between">
        <span className="font-medium">
          {totalErrorCount} item(s) failed
          {truncated && ` (showing first ${errors.length})`}
        </span>
        {errors.length > 3 && (
          <button
            type="button"
            onClick={onToggle}
            className="text-[11px] underline"
          >
            {expanded ? 'Show fewer' : `Show all (${errors.length})`}
          </button>
        )}
      </div>
      <ul className="mt-1 space-y-1">
        {visible.map((err) => (
          <li key={err.externalId} className="break-words">
            <code className="font-mono text-[11px]">{err.externalId}</code>{' '}
            &mdash; {err.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
